import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Namespace, useT } from "@racoon/i18n";
import { EditFieldModal } from "~/shared/components/edit-field-modal";
import { useCreateSuspiciousLocationMutation } from "~/shared/api/govDash/govDash.api";
import { SuspiciousSiteMap } from "./suspicious-site-map";
import { convertLeafletToGeoJson } from "../utils/coordinateConversion";
import { parseTwoGisCoords } from "@racoon/lib";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";

type Tab = "general" | "map";

type SuspiciousLocationFormProps = {
  onBack: () => void;
  onSuccess?: () => void;
};

export const SuspiciousLocationForm = ({ onBack, onSuccess }: SuspiciousLocationFormProps) => {
  const t = useT();
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingGeofence, setIsEditingGeofence] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [modalValue, setModalValue] = useState("");

  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const [createSuspiciousLocation] = useCreateSuspiciousLocationMutation();

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    gisLink: "",
    latitude: 43.238949,
    longitude: 76.938642,
  });

  const [boundaryCoordinates, setBoundaryCoordinates] = useState<[number, number][]>([]);

  // Parse 2GIS link and update coordinates
  useEffect(() => {
    if (formData.gisLink.trim()) {
      const coords = parseTwoGisCoords(formData.gisLink);
      if (coords) {
        setFormData((prev) => ({
          ...prev,
          latitude: coords.lat,
          longitude: coords.lon,
        }));
      }
    }
  }, [formData.gisLink]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleGeofenceUpdate = (coordinates: [number, number][]) => {
    setBoundaryCoordinates(coordinates);
  };

  // Memoize center array to prevent unnecessary map re-renders
  const center = useMemo(
    () => [formData.latitude, formData.longitude] as [number, number],
    [formData.latitude, formData.longitude],
  );

  const openEditModal = (field: string, fieldLabel: string, value: string) => {
    setEditingField(field);
    setModalValue(value);
    setIsModalOpen(true);
  };

  const handleModalSave = () => {
    if (editingField) {
      handleInputChange(editingField, modalValue);
    }
    setIsModalOpen(false);
    setEditingField(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingField(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Заполните обязательное поле: Название");
      return;
    }

    if (!Number.isFinite(formData.latitude) || !Number.isFinite(formData.longitude)) {
      setError("Заполните обязательное поле: Координаты");
      return;
    }

    setIsLoading(true);
    try {
      // Build geometry from boundary coordinates if available
      let geometry: any = undefined;
      if (boundaryCoordinates.length > 2) {
        // Convert from Leaflet [lat, lon] to GeoJSON [lon, lat]
        const geoJsonCoords = convertLeafletToGeoJson(boundaryCoordinates);
        // Close the ring for valid GeoJSON polygon (repeat first point as last)
        const closedRing = [
          ...geoJsonCoords,
          ...(geoJsonCoords.length > 0 ? [geoJsonCoords[0]] : []),
        ];
        geometry = {
          coordinates: [closedRing],
          type: "Polygon",
        };
      }

      await createSuspiciousLocation({
        name: formData.name,
        type: 4, // LOCATION_TYPE_SUSPICIOUS
        lat: formData.latitude,
        lon: formData.longitude,
        address: formData.address || undefined,
        // gisLink: formData.gisLink || undefined,
        geometry,
      }).unwrap();

      onSuccess?.();
      onBack();
    } catch (err: any) {
      setError(err?.message || "Ошибка при сохранении");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="absolute inset-0 gap-2 flex flex-col bg-[#f2f4f7] z-50">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2">
        <button
          type="button"
          aria-label={t(Namespace.Govdash, "backToRecords")}
          onClick={onBack}
          className="flex size-[18px] items-center justify-center rounded-full bg-slate-900 text-white"
        >
          <ChevronLeft className="size-3" strokeWidth={2.5} aria-hidden />
        </button>
        <div className="flex flex-1 flex-col gap-1 items-center">
          <h1 className="text-[16px] font-semibold leading-4 tracking-[-0.16px] text-slate-900">
            Создать участок
          </h1>
          <p className="text-[12px] font-bold leading-4 tracking-0 text-[#727986] uppercase">
            подозрительный участок
          </p>
        </div>
        <div className="size-[24px]" />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm mx-2 mb-2 rounded-lg">
          {error}
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-start px-2 gap-0 overflow-y-auto">
        <div className="bg-white border border-[#e2e8f0] rounded-[16px] w-full overflow-hidden flex flex-col flex-1">
          {/* Tab Switcher */}
          <div className="flex p-2 gap-0">
            <div className="bg-[#d6e6ff] flex flex-1 rounded-[10px] p-1 gap-0">
              <button
                type="button"
                onClick={() => setActiveTab("general")}
                className={`flex-1 rounded-[8px] py-[10px] px-[10px] font-medium text-[14px] leading-4 tracking-[-0.14px] transition-colors ${
                  activeTab === "general"
                    ? "bg-[#007aff] text-white"
                    : "text-slate-900 hover:bg-opacity-50"
                }`}
              >
                Общее
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("map")}
                className={`flex-1 rounded-[8px] py-[10px] px-[10px] font-medium text-[14px] leading-4 tracking-[-0.14px] transition-colors ${
                  activeTab === "map"
                    ? "bg-[#007aff] text-white"
                    : "text-slate-900 hover:bg-opacity-50"
                }`}
              >
                Зона
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === "general" && (
            <div className="flex h-full flex-col px-4 py-2 gap-0">
              <div className="border border-[#e2e8f0] rounded-[12px] overflow-hidden">
                {/* Name Field */}
                <button
                  type="button"
                  onClick={() => openEditModal("name", "Название", formData.name)}
                  className="w-full flex h-[48px] items-center justify-between px-4 gap-2 bg-white border-b border-[#e2e8f0] hover:bg-[#f9fafb] active:bg-[#f3f4f6]"
                >
                  <div className="flex flex-1 flex-col items-start gap-0">
                    <span className="text-[14px] font-medium text-[#727986] leading-4 tracking-[-0.14px]">
                      Название
                    </span>
                  </div>
                  <span className="text-[14px] font-normal text-[#007aff] leading-4 tracking-[-0.14px]">
                    {formData.name || "Укажите название"}
                  </span>
                  <ChevronRight className="size-6 text-[#727986]" strokeWidth={2} />
                </button>

                {/* Address Field */}
                <button
                  type="button"
                  onClick={() => openEditModal("address", "Адрес", formData.address)}
                  className="w-full flex items-center justify-between px-4 py-2 gap-2 bg-white border-b border-[#e2e8f0] hover:bg-[#f9fafb] active:bg-[#f3f4f6]"
                >
                  <div className="flex flex-1 flex-col items-start gap-2">
                    <span className="text-[14px] font-medium text-[#727986] leading-4 tracking-[-0.14px]">
                      Адрес
                    </span>
                    <span className="text-[14px] font-normal text-[#007aff] leading-4 tracking-[-0.14px]">
                      {formData.address || "Укажите адрес"}
                    </span>
                  </div>
                  <ChevronRight className="size-6 text-[#727986]" strokeWidth={2} />
                </button>

                {/* Event Detection Field */}
                <button
                  type="button"
                  // onClick={() => openEditModal("gisLink", "Event Detection", formData.gisLink)}
                  className="w-full flex h-[48px] items-center justify-between px-4 gap-2 bg-white hover:bg-[#f9fafb] active:bg-[#f3f4f6]"
                >
                  <div className="flex flex-1 flex-col items-start gap-0">
                    <span className="text-[14px] font-medium text-[#727986] leading-4 tracking-[-0.14px]">
                      Обнаружение события
                    </span>
                  </div>
                  <span className="text-[14px] font-normal text-[#007aff] leading-4 tracking-[-0.14px]">
                    Стоянка
                  </span>
                  <ChevronRight className="size-6 text-[#727986]" strokeWidth={2} />
                </button>
              </div>
            </div>
          )}

          {/* Map Tab */}
          {activeTab === "map" && (
            <div className="flex-1 flex flex-col min-h-0 p-2">
              <div className="flex-1 relative min-h-0">
                <SuspiciousSiteMap
                  isEditingGeofence={isEditingGeofence}
                  boundaryCoordinates={boundaryCoordinates}
                  onGeofenceUpdate={handleGeofenceUpdate}
                  featureGroupRef={featureGroupRef}
                  center={center}
                />
                <button
                  type="button"
                  onClick={() => setIsEditingGeofence(!isEditingGeofence)}
                  className={`absolute bottom-6 left-1/2 transform -translate-x-1/2 h-10 px-6 rounded-lg font-semibold text-base leading-4 tracking-tight transition-colors z-[1000] ${
                    isEditingGeofence
                      ? "bg-green-500 text-white hover:bg-green-600"
                      : "bg-[#007aff] text-white hover:bg-blue-700"
                  }`}
                >
                  {isEditingGeofence
                    ? t(Namespace.Govdash, "actions.save")
                    : t(Namespace.Govdash, "actions.edit")}
                </button>
              </div>
            </div>
          )}

          {/* Create Button */}
          {/* <div className="flex items-center justify-center py-4 px-4">
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="bg-[#007aff] text-white font-semibold text-[16px] leading-4 tracking-[-0.16px] px-6 py-4 rounded-[8px] hover:bg-[#0063cc] active:bg-[#0051a8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Сохранение..." : "Создать"}
            </button>
          </div> */}
        </div>
        {/* Create Button */}
        <div className="flex w-full items-center justify-center py-4 px-4">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-[#007aff] text-white font-semibold text-[16px] leading-4 tracking-[-0.16px] px-6 py-4 rounded-[8px] hover:bg-[#0063cc] active:bg-[#0051a8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Сохранение..." : "Создать"}
          </button>
        </div>
      </div>

      <EditFieldModal
        isOpen={isModalOpen}
        fieldLabel={
          editingField === "name"
            ? "Название"
            : editingField === "address"
              ? "Адрес"
              : "Обнаружение события"
        }
        value={modalValue}
        onValueChange={setModalValue}
        onCancel={handleModalCancel}
        onSave={handleModalSave}
      />
    </div>
  );
};
